// Mirrors internal/kinds/project/project.go Apply.
import type { Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { formatTime } from '@/lib/time'
import { isProjectOpen, type ProjectSpec, type ProjectStatus } from './schema'

export type ProjectResource = Resource<ProjectSpec, ProjectStatus>

export const defaults = (spec: Partial<ProjectSpec>): ProjectSpec => ({ title: '', ...spec })

export const initialStatus = (r: ProjectResource): ProjectStatus =>
  compact({ state: 'active', open_tasks: 0, done_tasks: 0, last_touched: formatTime(new Date(r.created_at)) })

export const displayName = (r: ProjectResource) => r.spec.title

export const afterPatch = (r: ProjectResource, at: string): ProjectResource => ({
  ...r,
  status: compact({ ...r.status, last_touched: formatTime(new Date(at)) }),
})

export const apply = (r: ProjectResource, action: string, _args: unknown, at: string): ProjectResource => {
  const when = formatTime(new Date(at))
  const st: ProjectStatus = { ...r.status }
  switch (action) {
    case 'complete':
      if (!isProjectOpen(st.state)) throw conflict(`project is already ${st.state}`)
      st.state = 'done'
      st.completed_at = when
      break
    case 'drop':
      if (!isProjectOpen(st.state)) throw conflict(`project is already ${st.state}`)
      st.state = 'dropped'
      break
    case 'convert': {
      if (!isProjectOpen(st.state)) throw conflict(`project is already ${st.state}`)
      const ref = (_args ?? {}) as { kind?: unknown; id?: unknown }
      if (typeof ref.kind !== 'string' || !ref.kind || typeof ref.id !== 'string' || !ref.id)
        throw invalid('convert: kind and id required')
      st.state = 'dropped'
      st.converted_to = { kind: ref.kind, id: ref.id }
      break
    }
    case 'pause':
      if (st.state !== 'active') throw conflict(`project is ${st.state}`)
      st.state = 'paused'
      break
    case 'resume':
      if (st.state !== 'paused') throw conflict(`project is ${st.state}`)
      st.state = 'active'
      break
    case 'touch':
      break
    default:
      throw invalid(`unknown action ${action}`)
  }
  st.last_touched = when
  return { ...r, status: compact(st) }
}
